import dateFormat from "dateformat";
import moment from "moment";
import 'moment/locale/fr'
import { store } from '../store/store';

import { toast } from 'react-toastify'
import { config, TEAM_BOUND_SCOPES, GLOBAL_SCOPES, NEWS_CATEGORY_COLORS } from "./config";
import { Localize } from "../lang/langs";
const { customAlphabet } = require("nanoid")
const nanoid = customAlphabet("1234567890", 14)

/**
 * 
 * @param {*} date
 * @param {*} format
 * @param {*} timezone
 * @returns 
 */
export const formatDate = (date, format, timezone) => {
  moment.locale(localStorage.getItem("lang"))
  if (timezone)
    return moment(date).tz(timezone).format(format)
  return moment(date).format(format)
}

/**
 * 
 * @param {*} timestamp 
 * @returns 
 */
export const getDayPeriodFromDate = (timestamp) => {
  if (timestamp) {
    let date = new Date(timestamp)
    let hour = date.getHours()
    if (hour < 12) return "matin"
    else if (hour == 12) return "midi"
    return "soir"
  }
  return ""
}

/**
 * 
 * @param {*} date 
 * @returns 
 */
export const getFormattedDate = (date) => {
  if (date)
    return dateFormat(date, "dd-mm-yyyy")
  return null
}

/**
 * 
 * @param {*} date 
 * @param {*} format 
 * @returns 
 */
export const getVarFormattedDate = (date, format) => {
  return dateFormat(date, format)
}

/**
 * 
 * @param {*} seconds 
 * @returns 
 */
export async function wait(seconds) {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve();
    }, seconds * 1000);
  });
}

/**
 * 
 * @param {*} email 
 * @returns 
 */
export const validateEmail = (email) => {
  return String(email)
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    );
}

/**
 * Domain of every professional ("Mail Vital") address.
 */
export const VITAL_EMAIL_DOMAIN = "groupe-vital.com"

/**
 * Completes a professional email with the company domain, so only the local part
 * has to be typed: "rnakouzi", "rnakouzi@" and "rnakouzi@grou" all become
 * "rnakouzi@groupe-vital.com". An address already carrying another domain
 * (rnakouzi@gmail.com) is returned untouched — as is anything that cannot be
 * completed (empty value, no local part).
 * @param {*} value raw input value
 * @returns the completed address
 */
export function completeVitalEmail(value) {
  if (!value) return value
  const trimmed = String(value).trim()
  if (!trimmed) return trimmed

  const atIndex = trimmed.indexOf("@")
  if (atIndex < 0) return `${trimmed}@${VITAL_EMAIL_DOMAIN}`

  const localPart = trimmed.slice(0, atIndex)
  const domain = trimmed.slice(atIndex + 1).toLowerCase()
  if (!localPart) return trimmed

  // only complete while what was typed is still a prefix of the company domain
  return VITAL_EMAIL_DOMAIN.startsWith(domain) ? `${localPart}@${VITAL_EMAIL_DOMAIN}` : trimmed
}

/**
 *
 * @param {*} input_str
 * @returns
 */
export function validatePhoneNumber(input_str) {
  var re = /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/im;

  return re.test(input_str);
}

/**
 * 
 * @param {*} date 
 * @returns 
 */
export function getWeek(date) {
  if (!date) return ""
  var now = new moment(date)
  return now.week()
}

/**
 * 
 * @param {*} date 
 * @returns 
 */
export function getWeekYear(date) {
  if (!date) return ""
  var now = new moment(date)
  return now.endOf("week").year()
}

/**
 * 
 * @param {*} roleId 
 * @returns 
 */
export function isAdmin(roleId) {
  if (parseInt(roleId) > 2) {
    return false
  }
  return true
}

/**
 * 
 * @param {*} roleId 
 * @param {*} idCountry 
 * @returns 
 */
export function isOwner(roleId, idCountry) {
  let minRole = 1
  if (idCountry && ['2', '3'].includes(`${idCountry}`)) {
    minRole = 2
  }
  if (parseInt(roleId) > minRole) {
    return false
  }
  return true
}

/**
 * Helper function to define if its a working shift or not
 * @param {*} shift 
 * @returns 
 */
export function isWorkingShift(shift) {
  return !shift.absent &&
    !['R', 'AST', 'INT_AST', 'WE'].includes(shift.type) &&
    typeof shift.id_sample_shift != undefined &&
    shift.id_sample_shift != null
}

/**
 * Check that the collab keeps at least one continuous rest period of 35h (without working shift)
 * within the calendar week (Mon 00:00 - Sun 24:00) of the given shift.
 * Only the hours inside the week are counted.
 * @param {*} shift shift being added or updated
 * @param {*} planningList existing shifts
 * @param {*} pendingShifts other shifts being added in the same batch (not yet in planningList)
 * @returns {boolean}
 */
export function hasWeeklyRest(shift, planningList, pendingShifts = []) {
  if (!isWorkingShift(shift)) return true;
  const minRest = 35 * 60 * 60 * 1000;
  const weekStart = moment(shift.from).startOf('isoWeek').valueOf();
  const weekEnd = moment(shift.from).startOf('isoWeek').add(1, 'week').valueOf();

  const busy = planningList.concat(pendingShifts)
    .filter((s) => s.id !== shift.id &&
      isWorkingShift(s) &&
      s.id_collab === shift.id_collab &&
      (s.id_team === shift.id_team || s.id_team_delegate === shift.id_team_delegate) &&
      s.to > weekStart && s.from < weekEnd)
    .concat([shift])
    .sort((a, b) => a.from - b.from);

  // walk through the rest periods between working shifts, clipped to the week
  let restStart = weekStart;
  for (const s of busy) {
    if (Math.min(s.from, weekEnd) - restStart >= minRest) return true;
    restStart = Math.max(restStart, s.to);
  }
  return weekEnd - restStart >= minRest;
}

/**
 * 
 * @param {*} message 
 */
export function showSuccessToast(message) {
  toast.success(message)
}

/**
 * 
 * @param {*} message 
 */
export function showInfoToast(message) {
  toast.info(message)
}

/**
 * 
 * @param {*} message 
 */
export function showErrorToast(message) {
  toast.error(Localize((message) ? message : "An error has occured"))
}

/**
 * 
 * @returns 
 */
export function generateUniqueId() {
  return parseInt(nanoid())
}

/**
 * Collaborator status values.
 * "Actif" / "Inactif" are legacy values that have been replaced by "Présent" / "Parti";
 * "Prévu" marks a collaborator planned to join but not yet present.
 */
export const COLLAB_STATUS = {
  PLANNED: 'Prévu',
  PRESENT: 'Présent',
  GONE: 'Parti',
}

/**
 * Options for collaborator status dropdowns and column filters.
 * @returns {{value: string, label: string}[]}
 */
export const getCollabStatusOptions = () =>
  [COLLAB_STATUS.PLANNED, COLLAB_STATUS.PRESENT, COLLAB_STATUS.GONE]
    .map((value) => ({ value, label: Localize(value) }))

/**
 * A collaborator currently part of the workforce.
 * @param {*} statut
 * @returns {boolean}
 */
export const isCollabPresent = (statut) => statut === COLLAB_STATUS.PRESENT

/**
 * A collaborator that can be assigned to a shift or a mission: already present,
 * or planned to arrive.
 * @param {*} statut
 * @returns {boolean}
 */
export const isCollabPlannable = (statut) =>
  statut === COLLAB_STATUS.PRESENT || statut === COLLAB_STATUS.PLANNED

/**
 * Text color used for the status label in tables and detail views.
 * @param {*} statut
 * @returns {string}
 */
export const getCollabStatusColor = (statut) => {
  if (isCollabPresent(statut)) return 'green'
  if (statut === COLLAB_STATUS.PLANNED) return 'orange'
  return 'red'
}

/**
 * MUI palette name used for the status Chip / Tag color prop.
 * @param {*} statut
 * @returns {'success' | 'warning' | 'error' | 'default'}
 */
export const getCollabStatusMuiColor = (statut) => {
  if (isCollabPresent(statut)) return 'success'
  if (statut === COLLAB_STATUS.PLANNED) return 'warning'
  if (statut === COLLAB_STATUS.GONE) return 'error'
  return 'default'
}

/**
 * Tinted background / text / border / shadow used by the status Chip.
 * @param {*} statut
 * @returns {{backgroundColor: string, color: string, border: string, boxShadow: string}}
 */
export const getCollabStatusChipSx = (statut) => {
  if (isCollabPresent(statut))
    return {
      backgroundColor: 'rgba(46,125,50,0.12)',
      color: '#2e7d32',
      border: '1px solid rgba(46,125,50,0.20)',
      boxShadow: '0 2px 8px rgba(46,125,50,0.25)',
    }
  if (statut === COLLAB_STATUS.PLANNED)
    return {
      backgroundColor: 'rgba(237,108,2,0.12)',
      color: '#ed6c02',
      border: '1px solid rgba(237,108,2,0.22)',
      boxShadow: '0 2px 8px rgba(237,108,2,0.22)',
    }
  return {
    backgroundColor: 'rgba(198,40,40,0.10)',
    color: '#c62828',
    border: '1px solid rgba(198,40,40,0.18)',
    boxShadow: '0 2px 8px rgba(198,40,40,0.20)',
  }
}

/**
 * 
 * @param {*} status 
 * @returns 
 */
export const getTagClass = (status) => {
  switch (status) {
    case 'SUBMITTED':
    case 'PUBLISHED':
    case 'VALIDATED':
    case 'Présent':
    case 'signed':
    case 'yes':
    case 'Apte':
    case 'DONE':
      return 'success'
    case 'PENDING':
    case 'CREATED':
    case 'DELETE_REQUESTED':
    case 'formal notice':
    case 'warning':
    case 'sent':
    case 'externe':
    case 'Prévu':
    case 'Arrêt maladie longue durée':
      return 'warning'
    case 'REJECTED':
    case 'DELETED':
    case 'Parti':
    case 'recall the order':
    case 'Contrat suspendu':
    case 'Instance de départ':
    case 'no':
    case 'Closed':
    case 'Absent':
    case 'None':
    case 'Mise à pied':
      return 'danger'
    case 'formal notice':
    case 'Mi-temps thérapeutique':
    case 'interne':
    case 'CLIENT_CHANGE':
      return 'info'
    default:
      return null
  }
}

/**
 * 
 * @param {*} property 
 * @returns 
 */
export const dynamicSort = (property) => {
  let sortOrder = 1
  if (property[0] === "-") {
    sortOrder = -1
    property = property.substr(1)
  }

  if (!property || property === 'null') property = "id"

  return function (a, b) {

    let itemA = a[property]
    let itemB = b[property]
    if (typeof itemA === "string" && typeof itemB === "string") {
      itemA = itemA.toLowerCase()
      itemB = itemB.toLowerCase()
    }

    let result = (itemA < itemB) ? -1 : (itemA > itemB) ? 1 : 0
    return result * sortOrder
  }
}

/**
 * 
 * @param {*} dateTime 
 * @returns 
 */
export function getMinutesFromDate(dateTime) {
  let time = 0;
  let date = new Date(dateTime);
  if (date.getHours()) time += date.getHours() * 60;
  if (date.getMinutes()) time += date.getMinutes();
  return time;
}

/**
 * 
 * @param {*} value 
 * @returns 
 */
export function isEmpty(value) {
  return typeof value === 'undefined' || value === null
}

/**
 * 
 * @param {*} formattedDate 
 * @param {*} timeMinutes 
 * @returns 
 */
export function setAndReturnTimeForDate(formattedDate, timeMinutes) {
  let date = moment(formattedDate, 'DD/MM/YYYY')
  date.set({ hour: parseInt(timeMinutes / 60), minute: parseInt(timeMinutes % 60), second: 0, millisecond: 0 })
  return parseInt(date.format('x'))
}

/**
 * A recursive function to compare two items
 * returns true if equal and false if not
 * @param {*} itemA 
 * @param {*} itemB 
 * @returns 
 */
export function checkIfEqual(itemA, itemB) {

  let equal = true
  function checkEquality(a, b, key) {
    if (typeof a == 'object' && typeof b == 'object' && a != null && b != null) {
      if (Object.keys(a).length !== Object.keys(b).length) {
        equal = false
      } else {
        for (const key in a) {
          if (equal) checkEquality(a[key], b[key], key)
        }
      }
    } else if (a !== b) {
      equal = false
    }
  }
  checkEquality(itemA, itemB)
  return equal
}

/**
 * Function to return the calendar days difference between two dates
 * @param {Date} fromDate 
 * @param {Date} toDate 
 * @returns 
 */
export function getCalendarDaysDifference(fromDate, toDate) {
  let daysDiff = 0
  while (formatDate(fromDate, "DD-MM-YYYY") !== formatDate(toDate, "DD-MM-YYYY")) {
    if (fromDate > toDate) {
      fromDate.setDate(fromDate.getDate() - 1)
    } else if (toDate > fromDate) {
      toDate.setDate(toDate.getDate() - 1)
    }
    daysDiff++
  }
  return daysDiff
}

/**
 * Determines whether a shift's rendered block (the columns it visually spans on the planning
 * grid) covers the given day. This mirrors the day-by-day column-width logic used when laying
 * out shifts: stepping forward one day at a time while keeping the start clock-time. As a
 * result an overnight working shift (e.g. 19:00 → 07:00) renders as a single column on its
 * start day and therefore does NOT count as covering the following day — so the empty-day
 * placeholder is still shown there. Multi-day shifts/leaves remain covered on every day they
 * actually span.
 * @param {*} shift shift with `from`/`to` epoch millisecond timestamps
 * @param {number} dayStart start-of-day timestamp (ms) of the day being tested
 * @returns {boolean}
 */
export function shiftBlockCoversDay(shift, dayStart) {
  const from = new Date(shift.from)
  const to = new Date(shift.to)
  const fromDayStart = new Date(from.getFullYear(), from.getMonth(), from.getDate()).getTime()
  if (dayStart < fromDayStart) return false

  let lastCoveredDayStart = fromDayStart
  let cursor = new Date(shift.from)
  cursor.setDate(cursor.getDate() + 1)
  while (to >= cursor) {
    lastCoveredDayStart = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate()).getTime()
    cursor.setDate(cursor.getDate() + 1)
  }
  return dayStart <= lastCoveredDayStart
}

/**
 *
 * @param {*} userId
 * @param {*} key
 * @returns
 */
export function getUserPreferences(userId, key) {
  let userPreferences = JSON.parse(localStorage.getItem("USER_PREFERENCES"))
  if (!userPreferences || !userPreferences[userId]) return null
  return userPreferences[userId][key]
}

/**
 * 
 * @param {*} userId 
 * @param {*} key 
 * @param {*} value 
 */
export function setUserPreferences(userId, key, value) {
  let userPreferences = JSON.parse(localStorage.getItem("USER_PREFERENCES"))
  if (!userPreferences) userPreferences = {}
  if (!userPreferences[userId] || userPreferences[userId] == null) userPreferences[userId] = {}
  userPreferences[userId][key] = value
  localStorage.setItem("USER_PREFERENCES", JSON.stringify(userPreferences))
}

/**
 * 
 * @param {date} date 
 */
export function getLastUpdatedFromDate(date) {
  if (date && ["number", "string"].includes(typeof date)) {
    return moment(date).format("DD MMM YYYY HH:mm")
  }
  return null
}

/**
 * 
 * @param {*} list 
 * @param {*} key 
 * @returns 
 */
export function generateMapByKey(list, key) {
  let mapList = {}
  if (list && Array.isArray(list)) {
    for (let i = 0; i < list.length; i++) {
      let item = list[i]
      const keyValue = item[key]
      if (!isEmpty(keyValue) && !mapList[keyValue])
        mapList[keyValue] = item
    }
  }
  return mapList
}

/**
 * Function to get user permission to a scope with type
 * @param {string} scope mandatory
 * @param {string} type optional | default = "view"
 * @param {Array|string} idTeam optional | default is null
 */
export function checkPermission(scope, type = config.permissionTypes.view, idTeam = null) {

  if (!scope) return false
  let teamIds
  if (idTeam) {
    if (Array.isArray(idTeam)) {
      teamIds = idTeam
    } else {
      teamIds = [idTeam]
    }
  }

  if (store.getState().login.userRoleId <= 1) return true

  if ((scope === 'vcomp' || scope === 'vsecurite') && type !== config.permissionTypes.edit) return true

  let userPermissions = store.getState().login.permissions
  if (!userPermissions || !Array.isArray(userPermissions)) return false

  let isGlobal = GLOBAL_SCOPES.includes(scope)
  let isTeamBound = TEAM_BOUND_SCOPES.includes(scope)

  return userPermissions.some(p => {
    if (p.scope !== scope) return false
    // Type check: edit permission implies view access
    if (type === config.permissionTypes.edit && p.type !== config.permissionTypes.edit) return false

    // Global scope: team context is irrelevant
    if (isGlobal) return true

    // Team-bound scope
    if (isTeamBound) {
      // NULL id_team = "view all / edit all" across every team
      if (p.id_team === null || p.id_team === undefined) return true
      // Specific id_team: check against requested team(s)
      if (!idTeam) return true
      if (Array.isArray(idTeam)) return idTeam.some(id => id == p.id_team)
      return p.id_team == idTeam
    }

    return false
  })
}

export function noForFuture(month, year) {
  const currentDate = new Date();
  const inputDate = new Date(year, month - 1);

  if (inputDate.getTime() > currentDate.getTime()) {
    return false;
  } else {
    return true;
  }
}
export function pastYearConstrain(date) {
  const today = moment()
  const oneYearAgo = moment().subtract(1, 'years')
  const inputDate = moment(date)
  return inputDate.isSameOrAfter(oneYearAgo) && inputDate.isSameOrBefore(today)
}

export function blockageDaytoFutureMonth(from) {
  let now = moment()
  let blockageDay = store.getState().settings.blockageDay
  let monthEnd = moment(from, 'x').endOf('month').add(blockageDay, 'day')
  let monthStart = moment(from, 'x').startOf('month')
  let isEvaluationBlocked = now.isAfter(monthEnd) || now.isBefore(monthStart)
  //Enable 2024 as feature is getting launched
  isEvaluationBlocked = now.isBefore(monthStart) || monthStart.year() < 2024
  return !isEvaluationBlocked
}

export const capitalize = (s) => {
  if (typeof s !== 'string') return ''
  let capitalized = s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
  return Localize(capitalized)
}

export function safeParse(value, fallback) {
  try {
    if (typeof value === "string") {
      let jsonValue = JSON.parse(value)
      if (fallback && fallback[0] && Object.keys(fallback[0])) {
        // add missing keys
        if (jsonValue && jsonValue.length) {
          jsonValue.map(v => {
            Object.keys(fallback[0]).map(key => {
              if (!v[key]) {
                v[key] = fallback[0][key]
              }
            })
          })
        }
      }
      return jsonValue
    } else if (Array.isArray(value)) {
      return value
    }
    return fallback
  } catch (error) {
    console.error("Error parsing JSON:", value, error)
    return fallback
  }
}

/**
 * Check if a given day is within the range of any mission.
 *
 * @param {Array} missions - Array of missions with { start_date, end_date } in "DD/MM/YYYY" format.
 * @param {string} day - The date to check, in "DD/MM/YYYY" format.
 * @returns {boolean} - True if the day falls within any mission's range.
 */
export function isDayWithinAnyMission(missions, day) {
  const targetDate = moment(day, 'DD/MM/YYYY', true);

  if (!targetDate.isValid()) {
    console.warn('Invalid target date:', day);
    return false;
  }

  return missions.some(mission => {
    const startDate = moment(mission.start_date, 'DD/MM/YYYY', true);
    const endDate = mission.end_date
      ? moment(mission.end_date, 'DD/MM/YYYY', true)
      : moment();

    if (!startDate.isValid()) {
      console.warn('Invalid start date in mission:', mission.start_date);
      return false;
    }

    return targetDate.isBetween(startDate, endDate, undefined, '[]');
  })
}

/**
 * 
 * @param {*} yearHolidays 
 * @param {*} date 
 * @param {*} idCountry 
 */
export function checkIfPublicHoliday(yearHolidays, date, idCountry) {
  
  if (yearHolidays && yearHolidays[idCountry]) {
    for (let i = 0; i < yearHolidays[idCountry].length; i++) {
      const holiday = yearHolidays[idCountry][i]
      if (date.format("DD-MM-YYYY") === moment(holiday.date).format("DD-MM-YYYY"))
        return true
    }
  }

  return false
}

/**
 * Initials to show in place of a profile picture.
 *
 * Used when a collab has never uploaded one, and as the fallback when an upload exists but
 * fails to load. Takes first letters of the given and family name; falls back to whichever
 * one is present, and to "?" when neither is — a resource row always renders something.
 *
 * @param {string} prenom
 * @param {string} nom
 * @returns {string} one or two uppercase letters
 */
export function getCollabInitials(prenom, nom) {
  const first = (prenom || "").trim()
  const last = (nom || "").trim()

  // Locale-aware uppercase: these are French names, so accented initials must survive.
  const initial = (value) => value ? value.charAt(0).toLocaleUpperCase(localStorage.getItem("lang") || "fr") : ""

  const initials = `${initial(first)}${initial(last)}`
  return initials || "?"
}

// A fixed palette rather than an HSL hash: freeform tag/category strings (news categories,
// etc.) get a stable, readable color pair without ever landing on a muddy or low-contrast hue.
const TAG_COLOR_PALETTE = [
  { color: "#0B5FA5", background: "rgba(11, 95, 165, 0.10)" },
  { color: "#2E7D32", background: "rgba(46, 125, 50, 0.10)" },
  { color: "#B06000", background: "rgba(255, 144, 21, 0.12)" },
  { color: "#8E24AA", background: "rgba(142, 36, 170, 0.10)" },
  { color: "#D32F2F", background: "rgba(211, 47, 47, 0.10)" },
  { color: "#00838F", background: "rgba(0, 131, 143, 0.10)" },
  { color: "#5D4037", background: "rgba(93, 64, 55, 0.10)" },
]

export function getTagColorFromString(value) {
  const str = (value || "").trim()
  if (!str) return TAG_COLOR_PALETTE[0]
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 31 + str.charCodeAt(i)) | 0
  }
  return TAG_COLOR_PALETTE[Math.abs(hash) % TAG_COLOR_PALETTE.length]
}

// News categories are now a fixed, backend-validated list (NEWS_CATEGORY_COLORS): resolve
// through that explicit map first so each one stays visually stable, and only fall back to
// the hash palette for a legacy value that predates the fixed list.
export function getNewsCategoryColor(category) {
  return NEWS_CATEGORY_COLORS[category] || getTagColorFromString(category)
}

// Collabs are ordered by the `order` the backend stores for them, but that value is often
// null (nobody has ever reordered the team). `a.order - b.order` then yields NaN, which the
// sort treats as "equal", so the rows keep whatever arbitrary order the API happened to
// return and appear to shuffle on every refresh. Fall back to a deterministic key —
// unordered collabs go after ordered ones, then name, then id — so the list is stable.
export function compareCollabsByOrder(a, b) {
  const orderA = Number(a?.order)
  const orderB = Number(b?.order)
  const hasOrderA = Number.isFinite(orderA)
  const hasOrderB = Number.isFinite(orderB)

  if (hasOrderA && hasOrderB && orderA !== orderB) return orderA - orderB
  if (hasOrderA !== hasOrderB) return hasOrderA ? -1 : 1

  const nameA = `${a?.nom || ""} ${a?.prenom || ""}`.trim()
  const nameB = `${b?.nom || ""} ${b?.prenom || ""}`.trim()
  const byName = nameA.localeCompare(nameB, undefined, { sensitivity: "base" })
  if (byName !== 0) return byName

  return Number(a?.id || 0) - Number(b?.id || 0)
}
